import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = '/api';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        toast.success('Login successful!');
        navigate('/admin/dashboard');
      }
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      toast.error('Invalid password');
      setPassword('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-4" data-testid="admin-login-page">
      {/* Subtle red glow */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[400px] h-[400px] bg-red-900/10 rounded-full blur-3xl" />

      <div className="relative z-10 w-full max-w-md bg-[#0A0A0A] border border-white/10 p-8" data-testid="admin-login-card">
        {/* Logo/Icon */}
        <div className="flex justify-center mb-8">
          <div className="w-16 h-16 bg-red-900/30 border border-red-700/50 flex items-center justify-center">
            <Lock className="text-red-500" size={32} />
          </div>
        </div>

        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-heading uppercase tracking-wide text-white mb-2" data-testid="admin-login-title">Admin Login</h1>
          <p className="text-gray-500 text-sm">Enter password to access admin panel</p>
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="admin-login-form">
          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-gray-400 mb-2 block">
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-transparent border-white/20 text-white placeholder:text-gray-600 focus:border-red-600 focus:ring-1 focus:ring-red-600 rounded-none pr-12"
                required
                autoFocus
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-white transition-colors duration-200"
                data-testid="toggle-password-visibility"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-red-700 hover:bg-red-800 disabled:opacity-50 text-white font-bold uppercase tracking-[0.15em] text-sm py-4 transition-colors duration-200"
            data-testid="admin-login-submit"
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </button>
        </form>

        {/* Back to Home */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-sm text-gray-600 hover:text-white transition-colors duration-200"
            data-testid="back-to-home"
          >
            &larr; Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
